
import EditableInput from "@/components/ui/editable-input";
import { User, Mail, Phone, Edit, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Profile section component with separate edit state
interface ProfileSectionProps {
  isEditing?: boolean;
  onEditToggle?: () => void;
  onCancelEdit?: () => void;
}

const ProfileSection = ({
  isEditing = false,
  onEditToggle,
  onCancelEdit
}: ProfileSectionProps) => {
  const { userData } = useAuth();
  
  // Local state for form data
  const [formData, setFormData] = useState({
    first_name: userData?.first_name || "",
    last_name: userData?.last_name || "",
    email: userData?.email || "",
    phone: userData?.phone || ""
  });

  // Update local state when userData changes
  useEffect(() => {
    if (userData) {
      setFormData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        phone: userData.phone || ""
      });
    }
  }, [userData]);

  // Create update function that updates both Supabase and global context
  const updateUserProfile = async (updates: Partial<typeof formData>): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!userData?.id) {
        return { 
          success: false, 
          error: 'Utente non autenticato' 
        };
      }

      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userData.id);

      if (error) {
        console.error('Error updating user profile:', error);
        return { 
          success: false, 
          error: error.message || 'Impossibile aggiornare il profilo utente' 
        };
      }

      // Force refresh of global context by triggering auth state change
      // This will cause AuthContext to refetch userData from Supabase
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Trigger a small update to force context refresh
        await supabase.auth.updateUser({ data: { last_updated: new Date().toISOString() } });
      }

      return { success: true };
      
    } catch (error) {
      console.error('Unexpected error updating user profile:', error);
      return { 
        success: false, 
        error: 'Errore imprevisto durante l\'aggiornamento del profilo' 
      };
    }
  };

  const { saveWithDebounce } = useAutoSave({
    debounceTime: 400,
    showToast: true,
    saveFunction: updateUserProfile
  });

  const handleInputChange = (field: string) => (value: string) => {
    // Update local state immediately
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-save with debounce only when editing and data has actually changed
    if (isEditing && userData && userData[field as keyof typeof userData] !== value) {
      saveWithDebounce({ [field]: value });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-2">
            <User className="h-5 w-5" />
            Dati Anagrafici
          </h3>
          <p className="text-sm text-gray-600">Informazioni personali di base</p>
        </div>
        {onEditToggle && onCancelEdit && (
          <div className="flex gap-2">
            {isEditing ? (
              <Button
                variant="outline"
                onClick={onCancelEdit}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Annulla
              </Button>
            ) : (
              <Button
                onClick={onEditToggle}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Modifica
              </Button>
            )}
          </div>
        )}
      </div>
      
      {/* Email */}
      <EditableInput
        label="Email"
        value={formData.email}
        type="email"
        disabled={true}
        icon={Mail}
        isEditing={isEditing}
        onChange={handleInputChange('email')}
        placeholder="Inserisci la tua email"
      />
      {isEditing && (
        <p className="text-sm text-gray-500">
          L'email non può essere modificata
        </p>
      )}

      {/* Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <EditableInput
          label="Nome"
          value={formData.first_name}
          icon={User}
          isEditing={isEditing}
          onChange={handleInputChange('first_name')}
          placeholder="Inserisci il tuo nome"
        />
        
        <EditableInput
          label="Cognome"
          value={formData.last_name}
          icon={User}
          isEditing={isEditing}
          onChange={handleInputChange('last_name')}
          placeholder="Inserisci il tuo cognome"
        />
      </div>

      {/* Phone */}
      <EditableInput
        label="Telefono"
        value={formData.phone}
        type="tel"
        icon={Phone}
        isEditing={isEditing}
        onChange={handleInputChange('phone')}
        placeholder="Inserisci il tuo numero di telefono"
      />
    </div>
  );
};

export default ProfileSection;
